import {
  Notification,
  NotificationsResponse,
  notificationsAtom,
} from "@app/shared/atoms/notifications-atom";
import { parseError, URLS } from "@core/shared/utils";
import { errorToast } from "@design-system/components/toast";
import { useAction } from "@framework/shared/hooks";
import { trans } from "@mongez/localization";
import { useCallback } from "react";

export function useNotifications() {
  const submit = useAction(URLS.notifications.action);

  const seedUnreadCount = useCallback(async () => {
    try {
      const response = await fetch(URLS.notifications.unreadCount);

      if (!response.ok) return;

      const data: { count: number } = await response.json();

      notificationsAtom.change("unreadCount", data.count ?? 0);
    } catch {
      // Silent — the bell simply shows no badge if the seed fails.
    }
  }, []);

  const loadNotifications = useCallback(async () => {
    try {
      const response = await fetch(URLS.notifications.root);

      if (!response.ok) return;

      const data: NotificationsResponse & { unreadCount: number } =
        await response.json();

      notificationsAtom.merge({
        notifications: data.notifications ?? [],
        paginationInfo: data.paginationInfo ?? null,
        unreadCount: data.unreadCount ?? notificationsAtom.value.unreadCount,
      });
    } catch {
      errorToast(trans("notifications.failedToLoad"));
    }
  }, []);

  const markAsRead = useCallback(
    async (notification: Notification) => {
      if (notification.isRead) return;

      const current = notificationsAtom.value;

      // Optimistic update.
      notificationsAtom.merge({
        notifications: current.notifications.map((item) =>
          item.id === notification.id ? { ...item, isRead: true } : item,
        ),
        unreadCount: Math.max(0, current.unreadCount - 1),
      });

      const response = await submit({ action: "markSeen", id: notification.id });

      if (response.isOK && typeof response.data?.count === "number") {
        notificationsAtom.change("unreadCount", response.data.count);
      }
    },
    [submit],
  );

  const markAllAsRead = useCallback(async () => {
    const current = notificationsAtom.value;

    notificationsAtom.merge({
      notifications: current.notifications.map((item) => ({
        ...item,
        isRead: true,
      })),
      unreadCount: 0,
    });

    const response = await submit({ action: "markAllSeen" });

    if (response.isOK && typeof response.data?.count === "number") {
      notificationsAtom.change("unreadCount", response.data.count);
    }
  }, [submit]);

  const deleteNotification = useCallback(
    async (notification: Notification) => {
      const current = notificationsAtom.value;

      notificationsAtom.merge({
        notifications: current.notifications.filter(
          (item) => item.id !== notification.id,
        ),
        unreadCount: notification.isRead
          ? current.unreadCount
          : Math.max(0, current.unreadCount - 1),
      });

      const response = await submit({ action: "delete", id: notification.id });

      if (response.isOK && typeof response.data?.count === "number") {
        notificationsAtom.change("unreadCount", response.data.count);
      } else if (!response.isOK) {
        errorToast(parseError(response.error));
      }
    },
    [submit],
  );

  const clearAll = useCallback(async () => {
    const previous = notificationsAtom.value.notifications;

    // Optimistic clear.
    notificationsAtom.merge({ notifications: [], unreadCount: 0 });

    const response = await submit({ action: "deleteAll" });

    if (response.isOK && typeof response.data?.count === "number") {
      notificationsAtom.change("unreadCount", response.data.count);
    } else if (!response.isOK) {
      // Restore on failure.
      notificationsAtom.change("notifications", previous);
      errorToast(parseError(response.error));
    }
  }, [submit]);

  return {
    seedUnreadCount,
    loadNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAll,
  };
}
